import React from 'react';
import { Link } from 'react-router-dom';
import './TermsAndConditions.css';
import './PrivacyPolicy.css';
import Footer from '../Footer';
import { PRIVACY_VERSION } from '../../constants/legalVersions';
import { SUPPORT_EMAIL } from '../../constants/supportContact';

// Displayed in the hero and recorded against every account as PRIVACY_VERSION.
// Both move together: see constants/legalVersions.js.
const LAST_UPDATED_LABEL = '24 September 2026';

const sections = [
  {
    id: 'p1',
    navLabel: '1 · Scope',
    title: '1 · Introduction and scope',
    paragraphs: [
      'Haastia ("Haastia", "we", "us" or "our") provides a platform that helps customers discover and book service professionals, and helps professionals and businesses manage services, schedules, customers, teams, payments and referrals.',
      'This policy explains how Haastia collects, uses, discloses and retains personal information across our websites, public booking profiles, applications and related services (the "Services"). It applies whether you use Haastia as a visitor, customer, guest customer, professional, business owner, team member, recruiter or other account holder.',
      "Professionals and businesses use Haastia to manage their own customer relationships. They decide what services to offer, what booking questions to ask and how they use customer information in providing their services. Their own privacy notices may also apply to information you give them in connection with an appointment. This policy describes Haastia's handling of information through the Services; it does not replace a professional's or business's own notice.",
    ],
  },
  {
    id: 'p2',
    navLabel: '2 · What we collect',
    title: '2 · Information we collect',
    description:
      'What Haastia collects depends on how you use the Services. It may include:',
    definitions: [
      {
        term: 'Account and identity information',
        detail:
          'Your name, email address, username, hashed password, account role, platform role, industry and account status.',
      },
      {
        term: 'Contact and location information',
        detail:
          'A phone number, website, street or service address, city, state, ZIP code, service area and saved map coordinates.',
      },
      {
        term: 'Profile and business information',
        detail:
          'A display or business name, biography, tagline, profile image, gallery, services, prices, availability, policies, work-location type and booking instructions.',
      },
      {
        term: 'Booking and customer information',
        detail:
          'Customer contact details, appointment details, service addresses, booking-question responses, internal client notes, cancellations, rescheduling details and service history.',
      },
      {
        term: 'Payment and transaction information',
        detail:
          'Prices, tips, payment status, subscription status, refunds, disputes, payout status and identifiers provided by Stripe.',
      },
      {
        term: 'Team, referral and recruiter information',
        detail:
          'Business memberships, roles, permissions, invitations, referral codes, referral status, commissions and payout requests.',
      },
      {
        term: 'Content and communications',
        detail:
          'Uploaded images and files, support requests, feedback, and communications associated with an account or appointment.',
      },
      {
        term: 'Technical and usage information',
        detail:
          'Network, browser, device, session, request, performance and diagnostic information, as described in section 8.',
      },
      {
        term: 'Consent records',
        detail:
          'The date you accepted the terms and this policy, and which version of each you accepted.',
      },
    ],
    paragraphs: [
      'We collect information directly from you, from other users acting through the Services, automatically when the Services are used, and from service providers such as Stripe.',
    ],
  },
  {
    id: 'p3',
    navLabel: '3 · Booking',
    title: '3 · Information customers provide when booking',
    paragraphs: [
      "When a customer books or manages an appointment, Haastia may collect the customer's name, email address, phone number, account identifier, selected professional or business, selected service, requested date and time, service location or address, price, tip, payment selection and status, and booking status.",
      'A professional or business may ask custom questions as part of booking. Responses may include free-form text, numbers, yes-or-no answers, selections and uploaded images. Booking records may also include rescheduling or cancellation details and reasons, refund or dispute information, and instructions connected with the appointment.',
      'Customers may book through an account or as a guest where that option is available. A guest booking is managed through a unique link that is valid for 30 days. Anyone holding a valid management link can view and change that booking, including its contact details, answers, address and payment status, so keep the link private.',
      'Professionals and businesses may create or update appointments on a customer’s behalf. They may also keep customer records and private internal client notes to support service delivery. Those notes are visible to authorised professional or business users and are not shown on public profiles.',
    ],
  },
  {
    id: 'p4',
    navLabel: '4 · Professionals',
    title: '4 · Information professionals and businesses provide',
    description:
      'Professionals and businesses provide information used to create and operate their accounts and public booking pages, including:',
    bullets: [
      'a name, username, business or display name, biography, tagline, industry, phone number, website, profile image and gallery;',
      "business, studio or service-location information, including an address, city, state, ZIP code, map coordinates, service area, and whether services happen at a business location, at a customer's location, or another way;",
      'service names, descriptions, images, prices, durations, add-ons, travel charges, policies and booking guidance;',
      'availability, blocked time, advance-booking limits, cancellation settings, scheduling preferences and time zone;',
      'custom booking questions and instructions for customers;',
      'portfolio images, captions, alternative text and other profile content;',
      'subscription, payment, payout and Stripe Connect status; and',
      'referral, recruiter, onboarding and account-status information.',
    ],
    paragraphs: [
      'Professionals and businesses also receive and manage information about the customers who book with them, and are responsible for using it only for legitimate business and service-delivery purposes.',
    ],
  },
  {
    id: 'p5',
    navLabel: '5 · Teams',
    title: '5 · Team and business account information',
    paragraphs: [
      'Business owners and administrators may create a business workspace, invite members by email, connect professionals to the business, and assign roles, permissions, services, schedules, notification preferences and access scopes. Haastia stores membership and invitation status, owner and member relationships, permission settings, and the activity needed to operate the workspace. Invitations expire after 30 days.',
      'Access inside a business account depends on each member’s role, assignments and permissions. Depending on those settings, a team member may be able to see or manage business settings, services, schedules, appointments, customers, customer contact information, internal notes and financial information. Where financial access is switched off, financial fields are removed from what that member is shown.',
      'Recruiters and referral participants receive a referral code and can see information about accounts attributed to that code, including an email address, signup or conversion status, commission balance and payout-request history. Haastia uses this to operate its referral and recruiter programmes.',
    ],
  },
  {
    id: 'p6',
    navLabel: '6 · Payments',
    title: '6 · Payments and Stripe',
    paragraphs: [
      'Haastia uses Stripe and Stripe Connect for booking payments, tips, subscriptions, refunds, disputes, professional onboarding and payouts.',
      'Stripe collects payment-card details directly through Stripe-hosted payment components. Professionals using Stripe Connect may also give Stripe identity, tax, bank-account, business, ownership and verification information. Stripe handles that information under its own privacy policy and terms, and may use it for payment processing, compliance, fraud prevention and verification.',
      "Haastia receives and keeps the payment information it needs to run the Services: transaction amounts, tips, payment and subscription status, refund and dispute information, connected-account status, and Stripe customer, account, checkout, subscription, invoice and payment identifiers. Haastia sends Stripe what a transaction or connected-account setup requires, such as a customer's email address, booking and service identifiers, the amount, and a professional's contact and support details.",
      "Haastia's own database is not designed to hold full payment-card numbers. Information held by Stripe may remain subject to Stripe's legal, regulatory, fraud-prevention and recordkeeping requirements even after a Haastia account is deleted.",
    ],
  },
  {
    id: 'p7',
    navLabel: '7 · Uploads',
    title: '7 · Images, uploads and booking-question responses',
    paragraphs: [
      'Users may upload profile, portfolio, gallery and service images. Customers may upload images in response to booking questions. Support and feedback submissions may include images and .log, .txt, .json or .csv files, up to three attachments (images up to 10MB, other files up to 5MB each).',
      'Haastia uses Cloudinary to store, transform and deliver uploaded images and files. Profile, portfolio, gallery and service media is publicly available when it appears on a public profile or service page. Other uploaded media is intended only for the people involved in the relevant booking, workflow or support request, but anyone who obtains a valid media link may be able to open the file. Do not upload anything you would not want the intended recipients and these providers to hold.',
      'Images submitted in answer to booking questions are scheduled for deletion about 48 hours after the appointment. The booking record and its text answers are kept longer, as described in section 14. Profile, gallery and service media stays until the content or the account is deleted. Support attachments stay with the related support record or email.',
      'Custom questions and free-form answers can contain sensitive information. Customers should give only what the requested service reasonably needs. Professionals should ask only what is relevant to providing or safely performing their services, and should not use Haastia to collect information they do not need.',
    ],
  },
  {
    id: 'p8',
    navLabel: '8 · Automatic',
    title: '8 · Information collected automatically',
    paragraphs: [
      'Haastia and its hosting and monitoring providers process technical information whenever the Services are used.',
    ],
    subsections: [
      {
        heading: 'Application access logs',
        body: 'The API writes a log line for each request holding the date and time, the request method, the URL, the response status, response time and size, and a request identifier. In production the line also carries the client IP address and, for a signed-in request, the account identifier. Booking-management tokens and password-reset codes are stripped out of logged URLs. Fly.io and Vercel additionally process network, request, delivery and operational logs while hosting and serving the Services.',
      },
      {
        heading: 'Sign-up and sign-in records',
        body: 'When you create an account, Haastia records the IP address the sign-up came from, a description of the browser and device you used (the "user agent" your browser sends), and when the sign-up happened. Each time you sign in we also record the time of your most recent sign-in and how many times you have signed in. Only Haastia staff can see this information. We use it to spot fake or automated sign-ups, prevent fraud and abuse, and keep accounts secure. We do not use it for advertising, we do not share it with other users, and we do not look up your location from it. The IP address and device description are deleted after 12 months, as described in section 14.',
      },
      {
        heading: 'Error and performance monitoring',
        body: 'Haastia uses Sentry to diagnose errors and performance problems. Sentry is deliberately configured not to identify you: events carry no account identifier, email address or IP address, and request bodies, cookies and authorization headers are removed before an event is sent. Booking-management tokens, password-reset codes and email addresses are redacted from URLs, breadcrumbs and transaction names. What Sentry receives is technical — browser and device details, the route involved, error messages, stack traces, browser console messages and related diagnostic context. Form contents are not sent to Sentry, though a value can still appear inside an error message that mentions it.',
      },
    ],
    note:
      'Haastia does not load any analytics, advertising or tag-management library, and does not build advertising profiles.',
  },
  {
    id: 'p9',
    navLabel: '9 · Cookies',
    title: '9 · Cookies and browser storage',
    paragraphs: [
      'Haastia sets one cookie, haastia_auth, which keeps you signed in. It is an HTTP-only cookie that expires after 24 hours, or after up to 30 days when you choose to stay signed in. Haastia sets no advertising or analytics cookies.',
      'Haastia also uses your browser’s local and session storage for your authentication token and for product state: the active account or business view, onboarding progress, pending signup status and dismissed interface messages. That data stays in your browser.',
      'These technologies authenticate you, keep your session, remember your choices and make the Services work. Blocking cookies or clearing browser storage will sign you out, drop saved preferences and may stop parts of the Services working.',
      'Stripe, Sentry, Vercel, Fly.io and Google Fonts may use network requests, cookies or similar technologies as part of providing their services. Haastia does not respond to browser "Do Not Track" signals, because there is no agreed standard for interpreting them. Where a legally recognised opt-out signal applies to our practices, we will handle it as the law requires.',
    ],
  },
  {
    id: 'p10',
    navLabel: '10 · How we use it',
    title: '10 · How Haastia uses information',
    description: 'Haastia uses personal information to:',
    bullets: [
      'create, authenticate, maintain and secure accounts and sessions;',
      'publish professional and business booking pages and help customers find services;',
      'schedule, confirm, manage, cancel and reschedule appointments;',
      'let professionals and businesses manage services, schedules, customers, client notes, teams, permissions and business activity;',
      'process booking payments, tips, subscriptions, refunds, disputes and payouts through Stripe;',
      'send transactional and security messages — booking confirmations, reminders, invitations, receipts, password resets and account notices;',
      'operate referral, recruiter, trial and commission programmes;',
      'provide support, respond to feedback and resolve billing, ownership and technical issues;',
      'host, store, transform and deliver content and media;',
      'monitor reliability and performance, diagnose errors, prevent fraud or abuse — including detecting fake or automated sign-ups — and protect users and the Services;',
      'enforce our agreements and policies, and keep the transaction, notification, support and operational records we need; and',
      'comply with legal obligations and establish, exercise or defend legal claims.',
    ],
  },
  {
    id: 'p11',
    navLabel: '11 · Sharing',
    title: '11 · When and why information is shared',
    description:
      'Haastia discloses personal information only as set out here or at your direction. Haastia does not sell personal information.',
    definitions: [
      {
        term: 'Customers, professionals and businesses',
        detail:
          'Booking and customer information goes to the professional or business responsible for the service, and may be shared with their authorised owners, administrators and team members according to workspace roles and permissions. Customers can see their own bookings; anyone with a valid guest-management link can see the booking it belongs to.',
      },
      {
        term: 'Team and referral programmes',
        detail:
          'Business owners and authorised members receive what they need to run their workspace. Recruiters receive the email address and conversion status of accounts attributed to their code, with commission and payout information.',
      },
      {
        term: 'Public profiles',
        detail: 'Profile information is published as described in section 13.',
      },
      {
        term: 'Haastia personnel',
        detail:
          'A small number of Haastia administrators can see operational records needed to run the platform and support users: a list of active accounts, support tickets and their attachments, recruiter and referral records, payout requests, and trial-coupon and invite records. People who administer our provider accounts — database, hosting, media, payments, monitoring and email — also have operational access to what those systems hold.',
      },
      {
        term: 'Service providers',
        detail:
          'Vendors that provide hosting, storage, media delivery, payments, email and diagnostics, listed in section 12.',
      },
      {
        term: 'Legal, safety and protection of rights',
        detail:
          'Where reasonably necessary to comply with law or legal process, respond to a lawful request, prevent fraud or abuse, protect someone’s safety, enforce an agreement, or establish, exercise or defend legal claims.',
      },
      {
        term: 'Business transactions',
        detail:
          'As part of a merger, financing, acquisition, reorganisation, bankruptcy, sale of assets or similar transaction, subject to law and appropriate confidentiality protections.',
      },
      {
        term: 'At your direction',
        detail: 'Where you ask us to, or give valid consent.',
      },
    ],
  },
  {
    id: 'p12',
    navLabel: '12 · Providers',
    title: '12 · Service providers',
    description: 'Haastia relies on these providers to run the Services:',
    definitions: [
      { term: 'MongoDB Atlas', detail: "Hosts Haastia's application database." },
      { term: 'Fly.io', detail: "Hosts Haastia's API and its operational logs." },
      { term: 'Vercel', detail: "Hosts and delivers Haastia's web application." },
      {
        term: 'Stripe and Stripe Connect',
        detail: 'Process payments, subscriptions, verification, refunds, disputes and payouts.',
      },
      { term: 'Cloudinary', detail: 'Stores, transforms and delivers images and uploaded files.' },
      { term: 'Sentry', detail: 'Provides error monitoring and performance diagnostics.' },
      {
        term: 'Google and Gmail',
        detail: 'Deliver and hold transactional, account and support email.',
      },
      { term: 'Google Fonts', detail: 'Delivers the fonts used by the web application.' },
    ],
    paragraphs: [
      "These providers process information to perform services for Haastia. Some, particularly Stripe, also process information for their own compliance, security, fraud-prevention and legal purposes under their own notices.",
    ],
  },
  {
    id: 'p13',
    navLabel: '13 · Public profiles',
    title: '13 · Public professional and business profiles',
    description:
      'A professional or business booking page is meant to be public. Depending on what the profile holds, that can include:',
    bullets: [
      'name, username, business or display name, profile image, biography, tagline, industry, phone number and website;',
      'portfolio, gallery and service images, captions and alternative text;',
      'city, state, ZIP code, service area, work-location type and map coordinates;',
      'services, descriptions, prices, durations, add-ons, travel charges, policies, availability, booking guidance and booking questions; and',
      'profile appearance and other settings used to render the public booking page.',
    ],
    paragraphs: [
      'A full street address is removed from a public profile. Latitude and longitude are not. Coordinates can pinpoint a location far more precisely than a city or ZIP code does, so if you set your profile location from a private home address, treat that location as public. Review your profile carefully and do not use a private home location for a public-facing page unless you are comfortable with that.',
      'Public information can be viewed, copied, indexed and retained by other people and by search engines. Removing or changing a profile does not pull back copies that others already made.',
      'Customer appointment records, service addresses, booking answers and internal client notes are not public.',
    ],
  },
  {
    id: 'p14',
    navLabel: '14 · Retention',
    title: '14 · Data retention',
    description:
      'Haastia keeps personal information for as long as it reasonably needs to run and secure the Services, keep proper business and transaction records, resolve disputes, enforce agreements, comply with the law, and establish, exercise or defend legal claims. How long that is depends on the information and why it is held. Some records have fixed lifetimes:',
    bullets: [
      'a pending professional signup tied to a paid signup is deleted after about 24 hours;',
      'a signed-in session lasts 24 hours, or up to 30 days if you chose to stay signed in;',
      'a password-reset code expires after 15 minutes;',
      'an email-change confirmation link expires after 30 minutes, and the record behind it is removed about 24 hours later;',
      'a business invitation expires after 30 days;',
      'a guest booking-management link expires after 30 days;',
      'images answering booking questions are deleted about 48 hours after the appointment; and',
      'the IP address and device description recorded at sign-up are deleted 12 months after the sign-up, or sooner if the account is deleted.',
    ],
    paragraphs: [
      'Account and profile information stays while the account is active, including while it is deactivated. Profile, gallery and service media stays until the content or the account is deleted. Text booking answers, booking and customer records, support correspondence, payment and subscription records, dispute information, referral and payout records, notification history, audit records and operational logs can stay longer where they are needed for the purposes above.',
      "Information held by a provider may also persist under that provider's own terms, legal obligations, fraud-prevention rules, backup cycle or deletion process.",
    ],
  },
  {
    id: 'p15',
    navLabel: '15 · Deleting',
    title: '15 · Deactivating and deleting an account',
    subsections: [
      {
        heading: 'Deactivating',
        body: 'A professional can deactivate an account under Settings → Account. Deactivation takes the public booking page down and signs the account out, and can be scheduled for a later date or cancelled before it takes effect. Nothing is deleted — the profile, images, services and availability are all kept, and signing back in offers to reactivate.',
      },
      {
        heading: 'Deleting',
        body: 'A professional can delete an account permanently from the same screen. Deletion asks for a confirmation phrase and the account password, because a signed-in session on its own is not proof of intent. If the account owns a business that still has other members, deletion is refused until that business is closed or ownership is transferred. Customers and guests, and anyone who cannot reach that screen, can ask us to delete an account by emailing us. We may need to verify identity and authority first.',
      },
      {
        heading: 'What deletion removes',
        body: 'Deleting an account removes the user record and the profile, services, availability, blocked time, client notes, reviews, feedback and recruiter-profile records attached to it, along with the Cloudinary media Haastia holds for it. Referral attribution for the account is switched off. If the account was a customer, retained bookings are detached from it. If the account was the professional on future appointments, those appointments are cancelled.',
      },
      {
        heading: 'What deletion keeps',
        body: 'Deletion does not clear every record in every system. Haastia keeps booking, transaction, payment, subscription, refund, dispute, notification, audit, fraud-prevention, safety and legal records where section 14 justifies it. A retained booking is detached from the deleted account, but it can still hold the service address, text answers to booking questions, cancellation notes, prices and payment status — so treat it as retained information rather than anonymised information. Stripe and other providers keep what they hold under their own obligations. Information a professional or business independently keeps about its customers is that professional’s or business’s responsibility.',
      },
    ],
  },
  {
    id: 'p16',
    navLabel: '16 · Security',
    title: '16 · Security',
    paragraphs: [
      'Haastia uses administrative, technical and organisational measures designed to protect personal information. These include account authentication, role and permission controls, hashed passwords, HTTP-only session cookies, expiring and hashed security and booking-management tokens, rate limiting, and Stripe-hosted components for collecting card details so card numbers never reach our servers.',
      'No transmission or storage system is completely secure and we cannot guarantee absolute security. Protect your password, devices, email account and guest booking links, and tell us promptly if you think an account or booking link has been compromised.',
    ],
    highlighted: true,
    cta: { label: 'Report a security concern', to: '/contact' },
  },
  {
    id: 'p17',
    navLabel: '17 · Your rights',
    title: '17 · Privacy rights and requests',
    paragraphs: [
      'Depending on where you live, you may have the right to ask for access to your personal information, correction of what is inaccurate, deletion, a portable copy, restriction of or objection to certain processing, withdrawal of consent, or information about how your personal information is used and disclosed.',
      'You can change much of your account, profile, business, scheduling and public-profile information yourself in the Services. For anything else, email us and describe what you want. We may ask for what we reasonably need to verify your identity, your authority and which account is involved. An authorised agent may make a request where the law allows, with proof of authorisation.',
      'These rights are not absolute. We may decline or limit a request where the law permits — for example where information has to be kept for transactions, security, fraud prevention, legal obligations, disputes or the rights of others. We will not discriminate against you for exercising a privacy right.',
      'Where a professional or business controls how customer information is used for its own service relationship, you may need to take a request to them directly. We can help them respond to a request about information processed through the Services.',
    ],
  },
  {
    id: 'p18',
    navLabel: '18 · U.S. states',
    title: '18 · U.S. state privacy disclosures',
    description:
      'This section applies where a U.S. state privacy law covers our activities and gives you rights. In the preceding 12 months Haastia may have collected these categories, each described more fully above:',
    bullets: [
      'identifiers and contact information;',
      'account credentials and security information;',
      'customer, appointment and service records;',
      'commercial, subscription, payment and transaction information;',
      'internet, network, device, session and diagnostic information;',
      'location information, including service addresses and map coordinates;',
      'professional, business, team, referral and recruiter information; and',
      'user-generated content, images, files, booking answers, support communications and internal client notes.',
    ],
    paragraphs: [
      'We obtain these from users, professionals and businesses, business owners and team members, recruiters, browsers and devices, Stripe, and the providers in section 12, and we use and disclose them for the purposes in sections 10 to 12.',
      'Depending on their contents and the applicable law, authentication information, payment or financial information, precise location information, free-form booking answers and uploaded files may count as sensitive personal information. We use sensitive personal information to provide, secure and support the Services, and not to infer unrelated personal characteristics.',
      'Haastia does not sell personal information for money. Haastia does not disclose personal information for cross-context behavioural or targeted advertising, and loads no advertising or analytics technology. If that ever changes we will update this policy and provide whatever notice and opt-out the law requires.',
      'Residents of a state with a comprehensive privacy law may have rights to know or access, correct, delete or obtain a portable copy of personal information, and to opt out of certain sales, sharing, targeted advertising or profiling. Some states also grant rights over sensitive information and a right of appeal. Email us to make a request. If you have a right to appeal a decision, reply to the decision email with the word "Appeal" and the basis for it.',
    ],
  },
  {
    id: 'p19',
    navLabel: '19 · Children',
    title: "19 · Children's privacy",
    paragraphs: [
      'The Services are intended for people aged 18 or over. They are not directed to children under 13, and Haastia does not knowingly collect personal information from children under 13.',
      'If we learn that we have collected personal information from a child in a way that breaks the law, we will take appropriate steps to delete it or otherwise deal with it. A parent or guardian who believes a child has given us personal information can contact us.',
    ],
  },
  {
    id: 'p20',
    navLabel: '20 · International',
    title: '20 · International users',
    paragraphs: [
      'Haastia and the providers in section 12 may process information in the United States and in other countries where they operate. Privacy laws in those countries may differ from the ones where you live.',
      'Where the law requires a legal basis for processing, we process personal information as needed to provide the Services you asked for and perform our agreements, to comply with legal obligations, with your consent where we ask for it, and for legitimate interests such as operating, securing, supporting and improving the Services and protecting users and Haastia. We balance those interests against the rights of the people affected.',
      'Email us to ask about your privacy rights or where your information is processed. We will give further information about transfer arrangements where the law requires it.',
    ],
  },
  {
    id: 'p21',
    navLabel: '21 · Changes',
    title: '21 · Changes and contact',
    paragraphs: [
      'We may update this policy to reflect changes in the Services, the law or our practices. The updated policy is posted here with a revised "Last updated" date, and we will give additional notice where the law requires it. Please check back from time to time.',
      'Questions, privacy requests, account-deletion requests and security concerns all go to the same place. Please include enough detail to identify the account and the request, and do not email passwords, full card numbers, government identification or anything else we have not asked for.',
    ],
    highlighted: true,
    cta: { label: 'Contact Haastia', to: '/contact' },
  },
];

const PrivacyPolicy = () => (
  <>
    <main className="terms-page">
      <section className="site-section site-section--hero terms-hero">
        <div className="site-inner">
          <div className="terms-hero__copy">
            <p className="eyebrow">Privacy policy</p>
            <h1>What we collect, why we hold it, and what you can ask us to do with it.</h1>
            <p className="lead terms-hero__lead">
              This policy covers everyone who uses Haastia — the customers booking through it, the
              professionals and teams running a business on it, and anyone who just visits a booking
              page. It sits alongside our{' '}
              <Link to="/terms">terms and conditions</Link>.
            </p>
            <p className="terms-hero__meta">
              Last updated {LAST_UPDATED_LABEL} · Version {PRIVACY_VERSION} · Questions go to{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          </div>
        </div>
      </section>

      <section className="site-section site-section--flush-top terms-content">
        <div className="site-inner">
          <div className="terms-layout">
            <nav className="terms-nav privacy-nav" aria-label="Sections">
              <p className="eyebrow eyebrow--muted">Sections</p>
              {sections.map((section) => (
                <a href={`#${section.id}`} key={section.id}>
                  {section.navLabel}
                </a>
              ))}
            </nav>

            <div className="terms-articles">
              {sections.map((section) => (
                <article
                  id={section.id}
                  key={section.id}
                  className={`panel terms-article privacy-article${
                    section.highlighted ? ' panel--surface' : ''
                  }`}
                >
                  <h2>{section.title}</h2>
                  {section.description && <p>{section.description}</p>}
                  {section.definitions && (
                    <dl className="privacy-list">
                      {section.definitions.map((entry) => (
                        <div key={entry.term}>
                          <dt>{entry.term}</dt>
                          <dd>{entry.detail}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {section.bullets && (
                    <ul className="dot-list">
                      {section.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.subsections &&
                    section.subsections.map((entry) => (
                      <React.Fragment key={entry.heading}>
                        <h3>{entry.heading}</h3>
                        <p>{entry.body}</p>
                      </React.Fragment>
                    ))}
                  {section.paragraphs &&
                    section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.note && <p className="privacy-article__note">{section.note}</p>}
                  {section.cta && (
                    <Link className="btn btn-primary terms-article__cta" to={section.cta.to}>
                      {section.cta.label}
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default PrivacyPolicy;
